import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, CheckCircle2, Loader2, Clock, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, brl, formatError } from "@/lib/api";

function useCountdown(expiresAt) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const tick = () => setLeft(Math.max(0, new Date(expiresAt).getTime() - Date.now()));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [expiresAt]);
  return left;
}

export default function PixPayment() {
  const { orderId } = useParams();
  const [pix, setPix] = useState(null);
  const [error, setError] = useState("");
  const [paid, setPaid] = useState(false);
  const created = useRef(false);
  const left = useCountdown(pix?.expires_at);

  const generate = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.post("/payments/pix", { order_id: orderId, origin_url: window.location.origin });
      setPix(data);
      if (data.payment_status === "paid") setPaid(true);
    } catch (e) {
      setError(formatError(e));
    }
  }, [orderId]);

  useEffect(() => {
    if (created.current) return;
    created.current = true;
    generate();
  }, [generate]);

  useEffect(() => {
    if (!pix || paid) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/mercadopago/status/${pix.order_number}`);
        if (data.payment_status === "paid") setPaid(true);
      } catch {
        /* keep polling */
      }
    }, 5000);
    return () => clearInterval(t);
  }, [pix, paid]);

  const copy = async () => {
    await navigator.clipboard.writeText(pix.qr_code).catch(() => {});
    toast.success("Código Pix copiado");
  };

  if (error)
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center" data-testid="pix-error">
        <p className="font-display text-2xl font-bold">Não foi possível gerar o Pix</p>
        <p className="mt-2 text-slate-600">{error}</p>
        <Button onClick={generate} className="mt-6 rounded-full" data-testid="btn-pix-retry"><RefreshCw className="h-4 w-4" /> Tentar novamente</Button>
      </div>
    );
  if (!pix) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#00B1A9]" /></div>;

  const trackLink = `/rastrear?numero=${pix.order_number}&email=${encodeURIComponent(pix.email)}`;
  if (paid)
    return (
      <div className="mx-auto max-w-xl px-4 py-24 text-center" data-testid="pix-paid">
        <CheckCircle2 className="mx-auto h-14 w-14 text-green-600" />
        <h1 className="mt-6 font-display text-3xl font-extrabold">Pix recebido!</h1>
        <p className="mt-2 text-slate-600">Pedido <span className="font-mono font-bold">#{pix.order_number}</span> confirmado. Em breve ele entra em produção.</p>
        <Link to={trackLink} className="mt-8 inline-flex h-12 items-center rounded-full bg-slate-900 px-7 font-bold text-white hover:bg-[#0099DD]" data-testid="pix-track-link">Acompanhar pedido</Link>
      </div>
    );

  const expired = left === 0;
  const mm = String(Math.floor(left / 60000)).padStart(2, "0");
  const ss = String(Math.floor((left % 60000) / 1000)).padStart(2, "0");
  return (
    <div className="mx-auto max-w-lg px-4 py-12" data-testid="pix-payment">
      <h1 className="font-display text-3xl font-extrabold tracking-tight">Pague com Pix</h1>
      <p className="text-slate-500">Pedido <span className="font-mono font-bold">#{pix.order_number}</span> · <span className="font-mono font-bold" data-testid="pix-total">{brl(pix.total)}</span></p>
      <div className="mt-6 space-y-5 rounded-md border border-slate-200 bg-white p-6 text-center">
        {expired ? (
          <div className="space-y-3 py-6" data-testid="pix-expired">
            <p className="font-semibold">Este código Pix expirou.</p>
            <Button onClick={generate} className="rounded-full bg-[#00B1A9] hover:bg-[#009690]" data-testid="btn-pix-new"><RefreshCw className="h-4 w-4" /> Gerar novo Pix</Button>
          </div>
        ) : (
          <>
            <img src={`data:image/png;base64,${pix.qr_code_base64}`} alt="QR Code Pix" className="mx-auto h-56 w-56" data-testid="pix-qr-image" />
            <p className="flex items-center justify-center gap-2 text-sm text-slate-500" data-testid="pix-countdown"><Clock className="h-4 w-4" /> Expira em {mm}:{ss}</p>
            <div className="rounded-md bg-slate-50 p-3 text-left">
              <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Pix copia e cola</p>
              <p className="break-all font-mono text-xs text-slate-700" data-testid="pix-code">{pix.qr_code}</p>
            </div>
            <Button onClick={copy} className="h-12 w-full rounded-full bg-[#00B1A9] text-base font-bold hover:bg-[#009690]" data-testid="btn-pix-copy"><Copy className="h-4 w-4" /> Copiar código Pix</Button>
            <p className="flex items-center justify-center gap-2 text-xs text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> Aguardando pagamento — esta tela atualiza sozinha.</p>
          </>
        )}
      </div>
      <p className="mt-4 text-center text-xs text-slate-500">Abra o app do seu banco, escolha Pix e leia o QR Code ou cole o código.</p>
    </div>
  );
}
